import { styled } from "@pigment-css/react";
import type { OrderPulseCalendar as OrderPulseCalendarSchema, PulseStatus } from "@acto/genui-components-schema";
import { useTranslations } from "@acto/web-common/locales";
import { daysSince } from "@acto/taskpane/field-sales-2/utils/format";
import {
  Card,
  Footer,
  FooterCol,
  FooterValue,
  Header,
  CardHeadline,
  MetaLabel,
} from "@acto/taskpane/field-sales-2/components/primitives/Card";
import { Divider } from "@acto/taskpane/field-sales-2/components/primitives/Divider";

const Grid = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.fieldSalesTwo.spacing[6],
  height: theme.fieldSalesTwo.spacing[26],
}));

const Slot = styled("div")({
  flex: "1 0 0",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
});

const Dot = styled("div")<{ status: PulseStatus }>(({ theme }) => ({
  borderRadius: theme.fieldSalesTwo.radius.circular,
  variants: [
    {
      props: { status: "active" },
      style: {
        width: 12,
        height: 12,
        background: theme.fieldSalesTwo.colors.neutral[900],
      },
    },
    {
      props: { status: "silent" },
      style: {
        width: 4,
        height: 4,
        background: theme.fieldSalesTwo.colors.neutral[300],
      },
    },
  ],
}));

interface OrderPulseCalendarProps {
  part: OrderPulseCalendarSchema;
}

export function OrderPulseCalendar({ part }: OrderPulseCalendarProps) {
  const t = useTranslations("fieldSalesTwo.genuiMeetingPrep.orderPulse");

  const silentDays = daysSince(part.lastOrder);

  const headlineText =
    part.normalCycle.minDays === part.normalCycle.maxDays
      ? t("cycleDriftHeadlineSingle", {
          days: part.normalCycle.minDays,
          drift: silentDays,
        })
      : t("cycleDriftHeadlineRange", {
          min: part.normalCycle.minDays,
          max: part.normalCycle.maxDays,
          drift: silentDays,
        });

  const normalCycleLabel =
    part.normalCycle.minDays === part.normalCycle.maxDays
      ? t("normalCycleSingle", { days: part.normalCycle.minDays })
      : t("normalCycleRange", {
          min: part.normalCycle.minDays,
          max: part.normalCycle.maxDays,
        });

  const rangeLabel = t("rangeValue", { weeks: part.weeks.length });

  return (
    <Card>
      <Header>
        <MetaLabel>{t("meta")}</MetaLabel>
        <CardHeadline>{headlineText}</CardHeadline>
      </Header>
      <Grid>
        {part.weeks.map((status, index) => (
          <Slot key={index}>
            <Dot status={status} />
          </Slot>
        ))}
      </Grid>
      <Divider />
      <Footer>
        <FooterCol>
          <MetaLabel>{t("normalLabel")}</MetaLabel>
          <FooterValue>{normalCycleLabel}</FooterValue>
        </FooterCol>
        <FooterCol>
          <MetaLabel>{t("rangeLabel")}</MetaLabel>
          <FooterValue>{rangeLabel}</FooterValue>
        </FooterCol>
      </Footer>
    </Card>
  );
}
